/**
 * CantiereGanttView — Vista Gantt + storico cronoprogrammi + tabella attività.
 *
 * Refactor v1.7.0: i tre modali sono ora file separati sotto `./gantt/`:
 *   - CronoFormModal      — crea/modifica cronoprogramma
 *   - AttivitaFormModal   — crea/modifica attività (date, orari, persone, nota importante)
 *   - AttivitaDetailModal — dettaglio READ-ONLY con bottone "Modifica"
 * Costanti condivise in `./gantt/ganttConstants.js`.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import GanttTimeline from "@/components/GanttTimeline";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { STATO_LABELS_GANTT } from "./gantt/ganttConstants";
import CronoFormModal from "./gantt/CronoFormModal";
import AttivitaFormModal from "./gantt/AttivitaFormModal";
import AttivitaDetailModal from "./gantt/AttivitaDetailModal";
import InlineNotaEditor from "./gantt/InlineNotaEditor";
import { LAVORAZIONI_STANDARD } from "./gantt/ganttConstants";

export default function GanttView({ cantiere, attivita }) {
  const [selectedCronoId, setSelectedCronoId] = React.useState(null);
  const [showCreateCrono, setShowCreateCrono] = React.useState(false);
  const [editCronoOpen, setEditCronoOpen] = React.useState(null);
  const [editAttOpen, setEditAttOpen] = React.useState(null);
  const [detailAttOpen, setDetailAttOpen] = React.useState(null);
  const [notaEditorFor, setNotaEditorFor] = React.useState(null);
  const [filters, setFilters] = React.useState({ stato: "", categoria: "", responsabile: "", ritardo: false, daFatturare: false });
  const [viewMode, setViewMode] = React.useState("giornaliera");
  const qc = useQueryClient();
  const cantId = cantiere.cantiere_id;

  const { data: cronoprogrammi = [] } = useQuery({
    queryKey: ["cronoprogrammi", cantId],
    queryFn: () => api.get(`/cronoprogramma/cronoprogrammi?cantiere_id=${cantId}`).then((r) => r.data),
  });
  const { data: legenda = [] } = useQuery({
    queryKey: ["gantt-legenda"],
    queryFn: () => api.get(`/cronoprogramma/stati/legenda`).then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });

  const currentCrono = React.useMemo(() => {
    if (selectedCronoId) return cronoprogrammi.find((c) => c.cronoprogramma_id === selectedCronoId);
    return cronoprogrammi.find((c) => c.attivo) || cronoprogrammi[0] || null;
  }, [selectedCronoId, cronoprogrammi]);

  const { data: attCrono = [] } = useQuery({
    queryKey: ["attivita-crono", cantId, currentCrono?.cronoprogramma_id],
    queryFn: () => {
      const cid = currentCrono?.cronoprogramma_id;
      const url = cid
        ? `/cronoprogramma?cantiere_id=${cantId}&cronoprogramma_id=${cid}`
        : `/cronoprogramma?cantiere_id=${cantId}`;
      return api.get(url).then((r) => r.data);
    },
    enabled: cronoprogrammi.length > 0 || attivita.length > 0,
  });
  const items = cronoprogrammi.length > 0 ? attCrono : attivita;
  const STANDARD_NAMES = [
    "Approntamento",
    "Demolizioni",
    "Costruzioni",
    "Impianto elettrico",
    "Impianto idraulico",
    "Impianto condizionamento",
    "Tinteggiature",
  ];

  const filteredItems = items.filter((a) => {
    if (filters.stato && a.stato !== filters.stato) return false;
    if (filters.categoria && (a.categoria || "").toLowerCase() !== filters.categoria.toLowerCase()) return false;
    if (filters.responsabile && !(a.responsabile || "").toLowerCase().includes(filters.responsabile.toLowerCase())) return false;
    if (filters.ritardo && !a.is_in_ritardo) return false;
    if (filters.daFatturare && !(a.importo > 0 && a.stato !== "eseguita")) return false;
    return true;
  });

  const activateCrono = useMutation({
    mutationFn: (id) => api.post(`/cronoprogramma/cronoprogrammi/${id}/attiva`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Cronoprogramma attivato");
      qc.invalidateQueries({ queryKey: ["cronoprogrammi", cantId] });
    },
  });
  const duplicaCrono = useMutation({
    mutationFn: (id) => api.post(`/cronoprogramma/cronoprogrammi/${id}/duplica`).then((r) => r.data),
    onSuccess: (d) => {
      toast.success("Cronoprogramma duplicato");
      qc.invalidateQueries({ queryKey: ["cronoprogrammi", cantId] });
      setSelectedCronoId(d.cronoprogramma_id);
    },
  });
  const deleteCrono = useMutation({
    mutationFn: (id) => api.delete(`/cronoprogramma/cronoprogrammi/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Cronoprogramma eliminato — dati IVA Previsionale ricalcolati");
      qc.invalidateQueries({ queryKey: ["cronoprogrammi", cantId] });
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantId] });
      setSelectedCronoId(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore eliminazione cronoprogramma"),
  });
  const deleteAttivita = useMutation({
    mutationFn: (id) => api.delete(`/cronoprogramma/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Voce eliminata — conteggi e IVA Previsionale aggiornati");
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantId] });
      qc.invalidateQueries({ queryKey: ["fatture", cantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Eliminazione fallita"),
  });
  const duplicaAttivita = useMutation({
    mutationFn: (id) => api.post(`/cronoprogramma/${id}/duplica`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Attività duplicata");
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
    },
  });

  const shiftAttivita = useMutation({
    mutationFn: ({ attivita_id, data_inizio, data_fine }) =>
      api.patch(`/cronoprogramma/${attivita_id}`, { data_inizio, data_fine }).then((r) => r.data),
    onSuccess: (_d, vars) => {
      toast.success(`Attività riprogrammata (${vars.shift_days > 0 ? "+" : ""}${vars.shift_days}gg)`);
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Riprogrammazione fallita"),
  });

  const resizeAttivita = useMutation({
    mutationFn: ({ attivita_id, data_inizio, data_fine }) =>
      api.patch(`/cronoprogramma/${attivita_id}`, { data_inizio, data_fine }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Date aggiornate");
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Aggiornamento date fallito"),
  });

  const reorderAttivita = useMutation({
    mutationFn: (items) => api.post(`/cronoprogramma/reorder`, { items }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Ordine aggiornato (${d?.updated || 0} lavorazioni)`);
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Riordino fallito"),
  });

  const linkDepsAttivita = useMutation({
    mutationFn: ({ attivita_id, dipendenze }) =>
      api.patch(`/cronoprogramma/${attivita_id}`, { dipendenze }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Dipendenza aggiornata");
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore dipendenza"),
  });

  // Seed rapido: crea le 7 lavorazioni standard scaglionate sul range del cronoprogramma.
  const seedLavorazioni = useMutation({
    mutationFn: async () => {
      if (!currentCrono?.cronoprogramma_id) throw new Error("Nessun cronoprogramma selezionato — crea prima un cronoprogramma.");
      const startISO = currentCrono.data_inizio_prevista || new Date().toISOString().slice(0, 10);
      const endISO = currentCrono.data_fine_prevista || new Date(new Date(startISO).getTime() + 210 * 86400000).toISOString().slice(0, 10);
      const startMs = new Date(startISO).getTime();
      const endMs = new Date(endISO).getTime();
      const totalDays = Math.max(7, Math.round((endMs - startMs) / 86400000));
      const step = Math.max(3, Math.floor(totalDays / LAVORAZIONI_STANDARD.length));
      const created = [];
      const existingNames = new Set(
        (items || []).map((a) => String(a.nome || "").trim().toLowerCase())
      );

      for (let i = 0; i < LAVORAZIONI_STANDARD.length; i++) {
        const l = LAVORAZIONI_STANDARD[i];
        const nomeKey = String(l.nome || "").trim().toLowerCase();

        // Non creare doppioni: aggiunge solo le lavorazioni mancanti
        if (existingNames.has(nomeKey)) continue;
        const di = new Date(startMs + i * step * 86400000).toISOString().slice(0, 10);
        const dfMs = Math.min(endMs, startMs + (i * step + Math.max(3, step - 1)) * 86400000);
        const df = new Date(dfMs).toISOString().slice(0, 10);
        // eslint-disable-next-line no-await-in-loop
        const r = await api.post("/cronoprogramma", {
          cantiere_id: cantId,
          cronoprogramma_id: currentCrono.cronoprogramma_id,
          nome: l.nome,
          categoria: l.categoria,
          data_inizio: di,
          data_fine: df,
          stato: "programmata",
          percentuale_avanzamento: 0,
          importo: 0,
        });
        created.push(r.data);
        existingNames.add(nomeKey);
      }
      return created;
    },
    onSuccess: (created) => {
      toast.success(`${created.length} lavorazioni standard generate`);
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Impossibile generare le lavorazioni"),
  });

  useLiveUpdates({
    rooms: [`cantiere:${cantId}`, "economia"],
    onEvent: (msg) => {
      if (!msg?.type) return;
      if (msg.type.startsWith("attivita.")) {
        qc.invalidateQueries({ queryKey: ["attivita-crono", cantId] });
        qc.invalidateQueries({ queryKey: ["crono-andamento", cantId] });
      }
      if (msg.type === "economia.changed") {
        qc.invalidateQueries({ queryKey: ["cantiere-econ", cantId] });
      }
      if (msg.type === "rapportino.created" || msg.type === "bolla.created") {
        qc.invalidateQueries({ queryKey: ["cantiere-econ", cantId] });
      }
    },
  });

  const dates = items.flatMap((a) => [a.data_inizio, a.data_fine]).filter(Boolean);
  const minD = dates.length ? new Date(Math.min(...dates.map((d) => new Date(d)))) : new Date();
  const maxD = dates.length ? new Date(Math.max(...dates.map((d) => new Date(d)))) : new Date();
  const totalDays = Math.max(1, Math.round((maxD - minD) / (1000 * 60 * 60 * 24)));

  return (
    <div className="space-y-4">
      {/* Testata + selettore cronoprogrammi */}
      <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="overline">Cronoprogramma</div>
          <h3 className="font-display font-bold text-lg">
            {currentCrono ? currentCrono.nome : "Attività di cantiere"}
            {currentCrono?.attivo && (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-white bg-emerald-600 px-1.5 py-0.5 rounded-sm">
                Attivo
              </span>
            )}
            {currentCrono?.versione && (
              <span className="ml-2 text-xs text-slate-500 font-normal">v.{currentCrono.versione}</span>
            )}
          </h3>
          {currentCrono?.descrizione && (
            <p className="text-xs text-slate-500 mt-0.5">{currentCrono.descrizione}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cronoprogrammi.length > 0 && (
            <select
              value={currentCrono?.cronoprogramma_id || ""}
              onChange={(e) => setSelectedCronoId(e.target.value)}
              data-testid="select-crono"
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm bg-white"
            >
              {cronoprogrammi.map((c) => (
                <option key={c.cronoprogramma_id} value={c.cronoprogramma_id}>
                  {c.nome} {c.attivo ? "★" : ""}
                </option>
              ))}
            </select>
          )}
          {currentCrono && !currentCrono.attivo && (
            <button
              onClick={() => activateCrono.mutate(currentCrono.cronoprogramma_id)}
              data-testid="btn-attiva-crono"
              className="px-3 py-1.5 text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"
            >
              Rendi attivo
            </button>
          )}
          {currentCrono && (
            <>
              <button
                onClick={() => setEditCronoOpen(currentCrono)}
                className="px-3 py-1.5 text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm"
                data-testid="btn-edit-crono"
              >
                Modifica
              </button>
              <button
                onClick={() => duplicaCrono.mutate(currentCrono.cronoprogramma_id)}
                className="px-3 py-1.5 text-xs font-semibold border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm"
              >
                Duplica
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Eliminare "${currentCrono.nome}"?`)) deleteCrono.mutate(currentCrono.cronoprogramma_id);
                }}
                className="px-3 py-1.5 text-xs font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm"
              >
                Elimina
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreateCrono(true)}
            data-testid="btn-create-crono"
            className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-sm"
          >
            + Crea nuovo cronoprogramma
          </button>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3 text-xs">
        <span className="font-bold text-slate-700 uppercase tracking-widest text-[10px]">Legenda:</span>
        {legenda.map((s) => (
          <div key={s.stato} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.colore }} />
            <span className="text-slate-700">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold uppercase tracking-widest text-slate-500">Filtri:</span>
        <select value={filters.stato} onChange={(e) => setFilters({ ...filters, stato: e.target.value })} className="px-2 py-1 border border-slate-300 rounded-sm bg-white">
          <option value="">Tutti gli stati</option>
          <option value="programmata">Programmata</option>
          <option value="in_corso">In corso</option>
          <option value="eseguita">Eseguita</option>
          <option value="consegnato">Consegnato</option>
          <option value="attesa_materiale">Attesa materiale</option>
        </select>
        <input placeholder="Categoria" value={filters.categoria} onChange={(e) => setFilters({ ...filters, categoria: e.target.value })} className="px-2 py-1 border border-slate-300 rounded-sm w-32" />
        <input placeholder="Responsabile" value={filters.responsabile} onChange={(e) => setFilters({ ...filters, responsabile: e.target.value })} className="px-2 py-1 border border-slate-300 rounded-sm w-32" />
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={filters.ritardo} onChange={(e) => setFilters({ ...filters, ritardo: e.target.checked })} /> in ritardo</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={filters.daFatturare} onChange={(e) => setFilters({ ...filters, daFatturare: e.target.checked })} /> da fatturare</label>
        <span className="ml-auto flex items-center gap-1">
          <span className="font-bold uppercase tracking-widest text-slate-500">Vista:</span>
          {["giornaliera","settimanale","mensile","trimestrale","annuale"].map((v) => (
            <button key={v} data-testid={`gantt-view-${v}`} onClick={() => setViewMode(v)} className={`px-2 py-1 rounded-sm text-[11px] ${viewMode === v ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{v}</button>
          ))}
        </span>
      </div>

      <div className="flex justify-end gap-2 flex-wrap">
        {currentCrono && (
          <button
            onClick={() => {
              if (window.confirm("Generare/completare le 7 lavorazioni standard?\n\nVerranno aggiunte solo quelle mancanti, senza creare duplicati.")) {
                seedLavorazioni.mutate();
              }
            }}
            disabled={seedLavorazioni.isPending}
            data-testid="btn-seed-lavorazioni"
            className="px-3 py-1.5 border border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 text-xs font-semibold rounded-sm disabled:opacity-50"
          >
            {seedLavorazioni.isPending ? "Generazione…" : "⚡ Genera/Completa 7 lavorazioni standard"}
          </button>
        )}
        <button
          onClick={() => setEditAttOpen({ cronoId: currentCrono?.cronoprogramma_id })}
          data-testid="btn-new-att"
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm"
        >
          + Nuova attività
        </button>
      </div>

      {filteredItems.length > 0 && (
        <GanttTimeline
          items={STANDARD_NAMES
            .map((nome) => filteredItems.find(
              (a) => String(a.nome || "").trim().toLowerCase() === nome.toLowerCase()
            ))
            .filter(Boolean)}
          viewMode={viewMode}
          onShiftDates={(payload) => shiftAttivita.mutateAsync(payload)}
          onResizeDates={(payload) => resizeAttivita.mutateAsync(payload)}
          onReorder={(items) => reorderAttivita.mutateAsync(items)}
          onLinkDeps={(payload) => linkDepsAttivita.mutateAsync(payload)}
          onRowClick={(att) => setDetailAttOpen(att)}
          onEditRow={(att) => setEditAttOpen({ attivita: att, cronoId: currentCrono?.cronoprogramma_id })}
          onDeleteRow={(att) => {
            if (window.confirm(`Eliminare la lavorazione "${att.nome}"?\n\nI conteggi (Gantt, Programma Lavori, IVA Previsionale) verranno aggiornati automaticamente.`)) {
              deleteAttivita.mutate(att.attivita_id);
            }
          }}
          readOnly={false}
        />
      )}

      {/* STORICO CRONOPROGRAMMI */}
      {cronoprogrammi.length > 0 && (
        <div className="bg-white border border-slate-200" data-testid="storico-cronoprogrammi">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="overline">Storico</div>
            <h4 className="font-display font-bold text-sm">Tutti i cronoprogrammi ({cronoprogrammi.length})</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Ogni nuovo cronoprogramma o aggiornamento resta salvato. Puoi passare tra le versioni,
              duplicarle e mantenere lo storico completo dell'evoluzione del cantiere.
            </p>
          </div>
          <div className="divide-y divide-slate-100">
            {[...cronoprogrammi]
              .sort((a, b) => new Date(b.data_creazione || 0) - new Date(a.data_creazione || 0))
              .map((c) => {
                const isActive = c.cronoprogramma_id === currentCrono?.cronoprogramma_id;
                const statoCls = {
                  attivo: "bg-emerald-100 text-emerald-800 border-emerald-300",
                  bozza: "bg-slate-100 text-slate-700 border-slate-300",
                  archiviato: "bg-slate-50 text-slate-500 border-slate-200",
                }[c.stato] || "bg-slate-100 text-slate-700 border-slate-300";
                return (
                  <div
                    key={c.cronoprogramma_id}
                    className={`px-3 py-2 flex items-center gap-3 flex-wrap ${isActive ? "bg-orange-50/60" : "hover:bg-slate-50"}`}
                    data-testid={`storico-crono-${c.cronoprogramma_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{c.nome}</span>
                        <span className="text-[10px] text-slate-500 font-mono">v.{c.versione}</span>
                        {c.attivo && (
                          <span className="text-[9px] font-bold uppercase tracking-widest text-white bg-emerald-600 px-1.5 py-0.5 rounded-sm">
                            ★ Attivo
                          </span>
                        )}
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${statoCls}`}>
                          {c.stato}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        {c.data_creazione && (
                          <span>Creato il {new Date(c.data_creazione).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        )}
                        {c.created_by_name && <span>· da {c.created_by_name}</span>}
                        {c.data_inizio_prevista && c.data_fine_prevista && (
                          <span>· {c.data_inizio_prevista} → {c.data_fine_prevista}</span>
                        )}
                        {c.responsabile && <span>· Resp. {c.responsabile}</span>}
                      </div>
                      {c.descrizione && (
                        <div className="text-[10px] text-slate-500 italic mt-0.5 line-clamp-1">{c.descrizione}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!isActive && (
                        <button
                          onClick={() => setSelectedCronoId(c.cronoprogramma_id)}
                          className="px-2 py-1 text-[10px] font-semibold border border-slate-300 text-slate-700 hover:bg-white rounded-sm"
                          data-testid={`btn-visualizza-crono-${c.cronoprogramma_id}`}
                        >
                          Visualizza
                        </button>
                      )}
                      {!c.attivo && (
                        <button
                          onClick={() => activateCrono.mutate(c.cronoprogramma_id)}
                          className="px-2 py-1 text-[10px] font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"
                        >
                          Rendi attivo
                        </button>
                      )}
                      <button
                        onClick={() => duplicaCrono.mutate(c.cronoprogramma_id)}
                        className="px-2 py-1 text-[10px] font-semibold border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm"
                      >
                        Duplica
                      </button>
                      <button
                        onClick={() => {
                          const nAtt = items.filter((a) => a.cronoprogramma_id === c.cronoprogramma_id).length;
                          const warn = c.attivo ? "\n\n⚠️ Questo è il cronoprogramma ATTIVO." : "";
                          const withAtt = nAtt > 0 ? `\n\nContiene ${nAtt} attività che verranno eliminate.` : "";
                          if (window.confirm(`Eliminare definitivamente "${c.nome}"?${withAtt}${warn}`)) {
                            deleteCrono.mutate(c.cronoprogramma_id);
                          }
                        }}
                        disabled={deleteCrono.isPending}
                        data-testid={`btn-elimina-crono-${c.cronoprogramma_id}`}
                        title="Elimina definitivamente questo cronoprogramma"
                        className="px-2 py-1 text-[10px] font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm disabled:opacity-50"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Tabella attività */}
      {filteredItems.length === 0 ? (
        <div className="p-8 bg-white border border-slate-200 text-center text-slate-500">
          {items.length === 0 ? 'Nessuna attività. Crea la prima con "+ Nuova attività".' : "Nessuna attività corrisponde ai filtri."}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200 bg-slate-50">
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 min-w-[220px]">Attività</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600">Categoria</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600">Stato</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600">Date</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 text-right">Durata</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 text-right">Importo</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 text-right">Ore eff/prev</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 text-right">Avanz.</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600">Timeline</th>
                <th className="p-2 text-xs uppercase tracking-widest text-slate-600 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((a) => {
                const start = new Date(a.data_inizio);
                const end = new Date(a.data_fine);
                const offset = Math.max(0, (start - minD) / (1000 * 60 * 60 * 24)) / totalDays * 100;
                const width = Math.max(1, (end - start) / (1000 * 60 * 60 * 24)) / totalDays * 100;
                const colore = a.colore_stato || "#94a3b8";
                return (
                  <tr key={a.attivita_id} className={`border-t border-slate-100 hover:bg-slate-50 ${a.is_in_ritardo ? "bg-rose-50/60" : ""}`} data-testid={`row-att-${a.attivita_id}`}>
                    <td className="p-2">
                      <div className="font-semibold flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDetailAttOpen(a)}
                          data-testid={`btn-dettaglio-att-${a.attivita_id}`}
                          title="Apri dettaglio attività"
                          className="text-left text-slate-900 hover:text-orange-700 hover:underline font-semibold"
                        >
                          {a.nome}
                        </button>
                        {a.is_in_ritardo && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-600 text-white text-[9px] font-bold uppercase tracking-wider rounded-sm" title={a.motivo_ritardo}>
                            ⚠ {a.giorni_ritardo}gg ritardo
                          </span>
                        )}
                      </div>
                      {a.responsabile && <div className="text-[10px] text-slate-500">👤 {a.responsabile}</div>}
                      {a.stato === "in_corso" && (a.giorni_trascorsi > 0 || a.giorni_residui > 0) && (
                        <div className="text-[10px] text-slate-500">
                          ⏱️ {a.giorni_trascorsi}gg trascorsi · {a.giorni_residui}gg residui
                        </div>
                      )}
                      {a.stato === "attesa_materiale" && a.attesa_materiale && (
                        <div className="text-[10px] text-slate-500">📦 {a.attesa_materiale.materiale_atteso} · {a.attesa_materiale.fornitore}</div>
                      )}
                    </td>
                    <td className="p-2 text-xs">{a.categoria || "—"}</td>
                    <td className="p-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold border" style={{ background: colore + "22", color: colore, borderColor: colore + "66" }}>
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: colore }} />
                        {STATO_LABELS_GANTT[a.stato] || a.stato}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-[11px] whitespace-nowrap">
                      {a.data_inizio} → {a.data_fine}
                      {a.data_fine_effettiva && (
                        <div className="text-[10px] text-emerald-700">Compl.: {a.data_fine_effettiva}</div>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono text-xs">
                      {a.durata_prevista_giorni ? `${a.durata_prevista_giorni}gg` : "—"}
                      {a.durata_effettiva_giorni && (
                        <div className="text-[10px] text-slate-500">eff: {a.durata_effettiva_giorni}gg</div>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono text-xs">
                      {a.importo > 0 ? `€${a.importo.toFixed(0)}` : "—"}
                      {a.data_fatturazione_prevista && (
                        <div className="text-[10px] text-slate-500">→ {a.data_fatturazione_prevista}</div>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono text-xs">
                      {(() => {
                        const eff = Number(a.ore_effettive_totali || 0);
                        const prev = Number(a.ore_previste || 0);
                        if (prev <= 0 && eff <= 0) return <span className="text-slate-400">—</span>;
                        const ratio = prev > 0 ? eff / prev : 0;
                        const color = prev <= 0 ? "text-slate-500" : ratio > 1 ? "text-rose-700" : ratio > 0.9 ? "text-amber-700" : "text-emerald-700";
                        return (
                          <span className={color} data-testid={`ore-eff-prev-${a.attivita_id}`} title={prev > 0 ? `${(ratio * 100).toFixed(0)}% delle ore preventivate` : "Nessuna previsione ore impostata"}>
                            {eff.toFixed(1)}h<span className="text-slate-400">/</span>{prev > 0 ? `${prev.toFixed(0)}h` : "—"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-right font-mono text-xs">{a.percentuale_avanzamentoo}%</td>
                    <td className="p-2 min-w-[220px]">
                      <div className="relative h-3 bg-slate-100 rounded-sm overflow-hidden">
                        <div
                          className="absolute h-full rounded-sm"
                          style={{ left: `${offset}%`, width: `${width}%`, background: colore }}
                          title={`${a.data_inizio} → ${a.data_fine}`}
                        >
                          <div className="h-full bg-black/20" style={{ width: `${a.percentuale_avanzamentoo}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {a.nota_importante ? (
                        <button
                          type="button"
                          onClick={() => setNotaEditorFor(a)}
                          title={`Nota: ${a.nota_importante}\n\nClicca per modificare/rimuovere`}
                          data-testid={`nota-badge-${a.attivita_id}`}
                          className={`inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full text-white text-[10px] font-black cursor-pointer ${a.nota_importante_rossa ? "bg-rose-600 animate-pulse" : "bg-amber-500"}`}
                        >
                          ⚠️
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setNotaEditorFor(a)}
                          title="Aggiungi una nota importante"
                          data-testid={`btn-add-nota-${a.attivita_id}`}
                          className="inline-flex items-center gap-0.5 text-slate-400 hover:text-amber-700 text-[10px] font-semibold mr-2 border border-dashed border-slate-300 hover:border-amber-400 px-1.5 py-0.5 rounded-sm"
                        >
                          + nota
                        </button>
                      )}
                      <button onClick={() => setEditAttOpen({ attivita: a, cronoId: currentCrono?.cronoprogramma_id })} className="text-blue-700 hover:text-blue-900 text-xs font-semibold mr-2">Modifica</button>
                      <button onClick={() => duplicaAttivita.mutate(a.attivita_id)} className="text-slate-700 hover:text-slate-900 text-xs mr-2">Duplica</button>
                      <button
                        onClick={() => {
                          if (window.confirm("Sei sicuro di voler eliminare questa voce dal cronoprogramma?\n\nI conteggi (Gantt, Programma Lavori, IVA Previsionale) verranno aggiornati automaticamente.")) {
                            deleteAttivita.mutate(a.attivita_id);
                          }
                        }}
                        data-testid={`btn-elimina-att-${a.attivita_id}`}
                        className="text-rose-600 hover:text-rose-800 text-xs font-semibold"
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreateCrono && <CronoFormModal cantiereId={cantId} onClose={() => setShowCreateCrono(false)} qc={qc} />}
      {editCronoOpen && <CronoFormModal cantiereId={cantId} onClose={() => setEditCronoOpen(null)} qc={qc} existing={editCronoOpen} />}
      {editAttOpen && <AttivitaFormModal cantiereId={cantId} cronoId={editAttOpen.cronoId} attivita={editAttOpen.attivita} onClose={() => setEditAttOpen(null)} qc={qc} />}
      {detailAttOpen && (
        <AttivitaDetailModal
          attivita={detailAttOpen}
          cantiere={cantiere}
          onClose={() => setDetailAttOpen(null)}
          onEdit={() => {
            setEditAttOpen({ attivita: detailAttOpen, cronoId: currentCrono?.cronoprogramma_id });
            setDetailAttOpen(null);
          }}
        />
      )}
      {notaEditorFor && (
        <InlineNotaEditor
          attivita={notaEditorFor}
          cantiereId={cantId}
          onClose={() => setNotaEditorFor(null)}
          qc={qc}
        />
      )}
    </div>
  );
}
